<script setup lang="ts">
import {computed, PropType, ref} from "vue";
import {CustomDTOExpression} from "@/utils/presenters/interfaces.ts";
import ListViewUtils from "@/utils/common/ListViewUtils.ts";
import Operator from "@/utils/constants/Operator.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import Translator from "@/utils/common/Translator.ts";
import AdvancedIcon from "@/components/icons/AdvancedIcon.vue";
import CriteriaDescriptionIcon from "@/components/icons/CriteriaDescriptionIcon.vue";
import AddRecordIcon from "@/assets/editViewIcons/AddRecordIcon.vue";
import BracketsIcon from "@/components/icons/BracketsIcon.vue";
import DeleteIcon from "@/assets/editViewIcons/DeleteIcon.vue";
import BusinessFunction from "@/utils/constants/BusinessFunction.ts";
import EnterIcon from "@/assets/editViewIcons/EnterIcon.vue";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";

const {compactMode, isUltra} = storeToRefs(useNamaFlagsStore());
const togglePadding = computed(() => isUltra.value ? '2px 5px' : compactMode.value ? '3px 6px' : '5px 8px');
const emit = defineEmits(["removeExpression", "appendIdInExpanded"]);
const relationOptions = [{label: 'AND', value: 'AND'}, {label: 'OR', value: 'OR'}];
const model = defineModel({
  type: Object as PropType<CustomDTOExpression>,
  required: true
})

const advancedModeEnabled = ref(false);
const showCriteriaDescription = ref(false);
const props = defineProps({
  ownerEntity: {type: String, required: true},
  root: {type: Boolean},
  expanded: {type: Boolean},
  isFilterEmpty: {type: Boolean, required: true},
  onCriteriaViewEnterDown: {type: Function, required: true}
});
function updateRelation(newValue: any) {
  model.value.relation = newValue ?? 'AND';
  if (model.value.children) {
    for (let child of model.value.children) {
      if (child.operator !== Operator.OpenBracket)
        child.relation = model.value.relation;
    }
  }
}

function appendExpression() {
  if (!model.value!.children)
    model.value.children = [];
  model.value.children.push({
    relation: model.value.relation,
    id: ListViewUtils.CRITERIA_EXP_ID++
  } as CustomDTOExpression);
  emit('appendIdInExpanded', model.value!.id);
}

function removeAllExpression() {
  model.value.children = [];
  if (!props.root)
    emit("removeExpression");
}

function appendExpressionGroup() {
  if (!model.value!.children)
    model.value.children = [];
  model.value.children.push({
    relation: model.value.relation,
    operator: Operator.OpenBracket,
    children: [],
    id: ListViewUtils.CRITERIA_EXP_ID++
  } as CustomDTOExpression);
}

function describeCriteria() {
  if (!model.value.children)
    return "";
  return describeExpressions(model.value.children, model.value);
}

function describeExpressions(expressions: CustomDTOExpression[], p: CustomDTOExpression) {
  let s = "";
  for (let child of expressions) {
    let childDesc = describeExpression(child);
    if (!childDesc)
      continue;
    if (s) {
      let relation = p.relation ?? "AND";
      if (Translator.isArabic()) {
        relation = relation === "OR" ? "أو" : "و";
      }
      s += " " + relation + " ";
    }
    s += childDesc;
  }
  return s;
}

function describeExpression(e: CustomDTOExpression) {
  if (!e)
    return "";
  if (e.children)
    return "(" + describeExpressions(e.children, e) + ")";
  if (!e.field || !e.operator)
    return;
  let fieldValue = NamaCommon.getFieldValue(e, e.field);
  if (fieldValue && fieldValue.id)
    fieldValue = Translator.translateRef(fieldValue);
  else if (typeof fieldValue === "boolean")
    fieldValue = Translator.translateId(fieldValue ? "TRUE" : "FALSE");
  else if (!fieldValue)
    fieldValue = '';
  return Translator.translateId(e.field, props.ownerEntity) + " " + Translator.translateId(e.operator) + " '" + fieldValue + "' ";
}
</script>

<template>
  <div class="tw-w-fit mobile:tw-w-full tw-flex tw-p-1 tw-px-3 compact:tw-px-2 ultra:tw-px-1.5 tw-rounded mobile:tw-flex-col tw-items-center tw-gap-3 compact:tw-gap-2 ultra:tw-gap-1.5" @click.stop>
    <div class="tw-flex mobile:tw-w-full tw-gap-2 ultra:tw-gap-1 tw-justify-start mobile:tw-overflow-x-auto">
      <q-btn-toggle
        @click.stop :padding="togglePadding"
        :model-value="model.relation"
        toggle-color="primary"
        class="tw-shadow-none tw-bg-namaBackground-dim-light dark:tw-bg-gray-600 tw-text-gray-700 tw-bg-gray-200 dark:tw-text-namaFontColor-input-dark tw-rounded"
        :class="{'tw-text-xs': isUltra}"
        @update:model-value="updateRelation" :options="relationOptions"/>
      <slot></slot>
      <a
        class="tw-content-center tw-bg-gray-200 hover:tw-bg-gray-300 active:tw-bg-gray-400 dark:tw-bg-gray-600 dark:hover:tw-bg-gray-500 dark:active:tw-bg-gray-400 tw-p-2 compact:tw-p-1.5 ultra:tw-p-1 tw-rounded-lg tw-cursor-pointer"
        @click="appendExpression" :title="Translator.translateId('addFiler')">
        <AddRecordIcon class="tw-size-5 compact:tw-size-5 ultra:tw-size-4 !tw-fill-gray-700 dark:!tw-fill-white"/>
      </a>
    <template v-if="advancedModeEnabled">
      <a
        class="tw-content-center tw-bg-gray-200 hover:tw-bg-gray-300 active:tw-bg-gray-400 dark:tw-bg-gray-600 dark:hover:tw-bg-gray-500 dark:active:tw-bg-gray-400 tw-p-2 compact:tw-p-1.5 ultra:tw-p-1 tw-rounded-lg tw-cursor-pointer"
        @click="appendExpressionGroup" :title="Translator.translateId('addFilterGroup')">
        <BracketsIcon class="tw-size-5 compact:tw-size-5 ultra:tw-size-4 !tw-fill-gray-700 dark:!tw-fill-white"/>
      </a>
      <a
        class="tw-content-center tw-bg-gray-200 hover:tw-bg-gray-300 active:tw-bg-gray-400 dark:tw-bg-gray-600 dark:hover:tw-bg-gray-500 dark:active:tw-bg-gray-400 tw-p-2 compact:tw-p-1.5 ultra:tw-p-1 tw-rounded-lg tw-cursor-pointer"
        @click="showCriteriaDescription = !showCriteriaDescription"
        :title="Translator.translateId(showCriteriaDescription?'hideCriteriaDescription':'viewCriteriaDescription')">
        <CriteriaDescriptionIcon class="tw-size-5 compact:tw-size-5 ultra:tw-size-4 !tw-fill-gray-700 dark:!tw-fill-white"/>
      </a>
    </template>
      <a
        class="tw-content-center  tw-bg-gray-200 hover:tw-bg-gray-300 active:tw-bg-gray-400 dark:tw-bg-gray-600 dark:hover:tw-bg-gray-500 dark:active:tw-bg-gray-400 tw-p-1.5 ultra:tw-p-1 tw-rounded-lg tw-cursor-pointer"
        @click="()=>advancedModeEnabled = !advancedModeEnabled"
        :title="Translator.translateId(advancedModeEnabled?'hideAdvancedCriteriaMode':'showAdvancedCriteriaMode')">
        <AdvancedIcon :enabled="advancedModeEnabled" class="tw-size-6 compact:tw-size-5 ultra:tw-size-5 !tw-fill-gray-700 dark:!tw-fill-white"/>
      </a>
      <a
        class="tw-bg-red-300 hover:tw-bg-red-400 active:tw-bg-red-500 dark:tw-bg-red-600 dark:hover:tw-bg-red-500 dark:active:tw-bg-red-400 tw-p-2 compact:tw-p-1.5 ultra:tw-p-1 tw-rounded-lg tw-cursor-pointer"
        @click="removeAllExpression" :title="Translator.translateId('clearFilters')">
        <DeleteIcon class="tw-size-5 compact:tw-size-5 ultra:tw-size-4 !tw-fill-gray-700 dark:!tw-fill-white"/>
      </a>
      <a v-if="isFilterEmpty"
         class="tw-content-center tw-bg-primary-light hover:tw-opacity-90 active:tw-opacity-95 dark:tw-bg-primary-dark tw-p-2 compact:tw-p-1.5 ultra:tw-p-1 tw-rounded-lg tw-cursor-pointer tw-w-[100px] ultra:tw-w-[80px]"
         @click="onCriteriaViewEnterDown()" :title="Translator.translateId(BusinessFunction.refresh)">
        <EnterIcon class="tw-size-4 !tw-fill-white tw-justify-self-center"/>
      </a>
    </div>
    <div class="tw-flex mobile:tw-w-full tw-gap-2" v-if="(root || !expanded) && showCriteriaDescription">
      <div class="dark:tw-text-namaFontColor-input-dark">{{ describeCriteria() }}</div>
    </div>
  </div>
</template>

<style scoped>

</style>
